import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import supabase from '../supabase/supabase';
import Navbar from '../components/storefront/Navbar';
import Footer from '../components/storefront/Footer';
import { FiPackage, FiCheckCircle, FiTruck, FiBox, FiXCircle, FiRefreshCw, FiLock, FiClock } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import LoadingSpinner from '../common/LoadingSpinner';

const STATUS_STEPS = [
  { key: 'confirmed', label: 'Confirmed', icon: FiCheckCircle, desc: 'Your order has been confirmed' },
  { key: 'packed', label: 'Packed', icon: FiBox, desc: 'Your order is being packed' },
  { key: 'shipped', label: 'Shipped', icon: FiTruck, desc: 'Your order is on the way' },
  { key: 'delivered', label: 'Delivered', icon: FiPackage, desc: 'Order delivered successfully' },
];

const LINK_EXPIRY_DAYS = 20;

const OrderTracking = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isOwner, setIsOwner] = useState(false);
  const [isExpired, setIsExpired] = useState(false);

  useEffect(() => {
    fetchOrder();
    const interval = setInterval(fetchOrder, 30000);
    return () => clearInterval(interval);
  }, [id]);

  const fetchOrder = async () => {
    try {
      const { data, error: fetchError } = await supabase
        .from('orders')
        .select('*, order_items(*, products(name, image, price))')
        .eq('id', id)
        .maybeSingle();

      if (fetchError) throw fetchError;
      if (!data) {
        setError('Order not found. Please check the link and try again.');
        setLoading(false);
        return;
      }

      const orderDate = new Date(data.created_at);
      const expiryDate = new Date(orderDate);
      expiryDate.setDate(expiryDate.getDate() + LINK_EXPIRY_DAYS);
      if (new Date() > expiryDate) {
        setIsExpired(true);
        setOrder(data);
        setLoading(false);
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        if (data.user_id === session.user.id) {
          setIsOwner(true);
        } else {
          const orderEmail = data.shipping_address?.email?.toLowerCase();
          const userEmail = session.user.email?.toLowerCase();
          if (orderEmail && userEmail && orderEmail === userEmail) {
            setIsOwner(true);
          }
        }
      }

      setOrder(data);
      setLoading(false);
    } catch (err) {
      setError('Unable to load order details.');
      setLoading(false);
    }
  };

  const getCurrentStepIndex = (status) => {
    const s = status?.toLowerCase();
    if (s === 'delivered') return 3;
    if (s === 'shipped') return 2;
    if (s === 'packed') return 1;
    if (s === 'confirmed') return 0;
    return -1;
  };

  if (loading) {
    return (
      <div className="bg-brand-sand min-h-screen font-sans flex flex-col pt-24">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner size="lg" text="Loading order details..." />
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-brand-sand min-h-screen font-sans flex flex-col pt-24">
        <Navbar />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="bg-white/90 p-12 rounded-3xl border border-white shadow-xl text-center max-w-md">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiXCircle} className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-2xl font-serif text-brand-darkSage mb-3">Order Not Found</h2>
            <p className="text-brand-lightText text-sm mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="bg-brand-darkSage text-white px-8 py-3 rounded-full text-xs font-bold uppercase tracking-widest hover:bg-brand-gold transition-all"
            >
              Return Home
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (isExpired) {
    const orderDate = new Date(order.created_at);
    const expiryDate = new Date(orderDate);
    expiryDate.setDate(expiryDate.getDate() + LINK_EXPIRY_DAYS);

    return (
      <div className="bg-brand-sand min-h-screen font-sans flex flex-col pt-24">
        <Navbar />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="bg-white/90 p-12 rounded-3xl border border-white shadow-xl text-center max-w-md">
            <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon={FiClock} className="w-8 h-8 text-amber-500" />
            </div>
            <h2 className="text-2xl font-serif text-brand-darkSage mb-3">Tracking Link Expired</h2>
            <p className="text-brand-lightText text-sm mb-2">
              This tracking link was valid for {LINK_EXPIRY_DAYS} days and expired on{' '}
              <strong>{expiryDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>.
            </p>
            <p className="text-brand-lightText text-xs mb-6">
              For order details, please log in to your account or contact our support team.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => navigate('/login')}
                className="bg-brand-darkSage text-white px-8 py-3 rounded-full text-xs font-bold uppercase tracking-widest hover:bg-brand-gold transition-all"
              >
                Log In
              </button>
              <button
                onClick={() => navigate('/')}
                className="bg-gray-100 text-brand-darkSage px-8 py-3 rounded-full text-xs font-bold uppercase tracking-widest hover:bg-gray-200 transition-all"
              >
                Return Home
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const currentStep = getCurrentStepIndex(order.status);
  const isCancelled = order.status === 'cancelled';
  const isDraft = order.status === 'draft';

  return (
    <div className="bg-brand-sand min-h-screen font-sans flex flex-col pt-24">
      <Navbar />
      <div className="flex-1 max-w-3xl mx-auto px-6 py-12 w-full">

        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-white/60 px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.2em] text-brand-gold border border-white shadow-sm mb-4">
            <SafeIcon icon={FiPackage} size={14} />
            Live Order Tracking
          </div>
          <h1 className="text-4xl font-serif text-brand-darkSage mb-2">
            Order #{order.id.split('-')[0].toUpperCase()}
          </h1>
          <p className="text-brand-lightText text-sm">
            Placed on {new Date(order.created_at).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        </div>

        {isCancelled ? (
          <div className="bg-red-50 border-2 border-red-100 rounded-3xl p-8 mb-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiXCircle} className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-2xl font-serif text-red-700 mb-2">Order Cancelled</h2>
            <p className="text-red-500 text-sm">This order has been cancelled.</p>
          </div>
        ) : isDraft ? (
          <div className="bg-amber-50 border-2 border-amber-100 rounded-3xl p-8 mb-8 text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiRefreshCw} className="w-8 h-8 text-amber-500" />
            </div>
            <h2 className="text-2xl font-serif text-amber-700 mb-2">Awaiting Payment</h2>
            <p className="text-amber-600 text-sm">This order is pending payment confirmation.</p>
          </div>
        ) : (
          <div className="bg-white/90 border border-white rounded-3xl p-8 mb-8 shadow-xl">
            <div className="flex items-center justify-between mb-8">
              <span className={`px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest border-2 ${
                order.status === 'delivered' ? 'bg-green-50 text-green-700 border-green-100' :
                order.status === 'shipped' ? 'bg-purple-50 text-purple-700 border-purple-100' :
                'bg-brand-gold/10 text-brand-gold border-brand-gold/20'
              }`}>
                {order.status}
              </span>
              <span className={`px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest border-2 ${
                order.payment_status === 'paid' ? 'bg-green-50 text-green-600 border-green-100' : 'bg-yellow-50 text-yellow-600 border-yellow-100'
              }`}>
                {order.payment_status === 'paid' ? 'Paid' : 'Payment Pending'}
              </span>
            </div>

            <div className="relative">
              <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-100"></div>
              <div
                className="absolute left-6 top-0 w-0.5 bg-brand-gold transition-all duration-1000"
                style={{ height: `${Math.min((currentStep / (STATUS_STEPS.length - 1)) * 100, 100)}%` }}
              ></div>

              <div className="space-y-8">
                {STATUS_STEPS.map((step, idx) => {
                  const isCompleted = idx < currentStep;
                  const isCurrent = idx === currentStep;
                  const Icon = step.icon;

                  return (
                    <div key={step.key} className="relative flex items-center gap-6">
                      <div className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-500 ${
                        isCompleted ? 'bg-brand-gold text-white shadow-lg shadow-brand-gold/30' :
                        isCurrent ? 'bg-brand-darkSage text-white shadow-lg shadow-brand-darkSage/30 ring-4 ring-brand-darkSage/10' :
                        'bg-gray-100 text-gray-400'
                      }`}>
                        <SafeIcon icon={Icon} size={20} />
                      </div>
                      <div className="flex-1">
                        <h3 className={`font-bold text-sm ${isCompleted || isCurrent ? 'text-brand-darkSage' : 'text-gray-400'}`}>
                          {step.label}
                        </h3>
                        <p className={`text-xs mt-0.5 ${isCompleted || isCurrent ? 'text-brand-lightText' : 'text-gray-300'}`}>
                          {step.desc}
                        </p>
                      </div>
                      {isCompleted && (
                        <div className="text-green-500">
                          <SafeIcon icon={FiCheckCircle} size={18} />
                        </div>
                      )}
                      {isCurrent && (
                        <span className="text-[9px] font-black uppercase tracking-widest text-brand-gold bg-brand-gold/10 px-3 py-1 rounded-full">
                          Current
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <div className="bg-white/90 border border-white rounded-3xl p-8 shadow-xl mb-8">
          <h3 className="text-lg font-serif text-brand-darkSage mb-6 flex items-center gap-3">
            <div className="w-8 h-8 bg-brand-gold/10 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiBox} className="text-brand-gold" size={16} />
            </div>
            Order Items
          </h3>
          <div className="space-y-4">
            {order.order_items?.map(item => (
              <div key={item.id} className="flex items-center gap-4 p-4 bg-brand-sand/30 rounded-2xl border border-brand-darkSage/5">
                <div className="w-16 h-16 bg-white rounded-xl p-2 border border-brand-darkSage/5 flex-shrink-0">
                  <img src={item.products?.image} alt={item.products?.name} className="w-full h-full object-contain" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-brand-darkSage text-sm truncate">{item.products?.name}</h4>
                  <p className="text-xs text-brand-lightText mt-0.5">Qty: {item.quantity} × Rs. {item.price.toFixed(2)}</p>
                </div>
                <p className="font-bold text-brand-darkSage text-sm">Rs. {(item.price * item.quantity).toFixed(2)}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-6 border-t border-brand-darkSage/5 flex justify-between items-center">
            <span className="text-sm font-bold text-brand-lightText">Total Amount</span>
            <span className="text-xl font-bold text-brand-darkSage">Rs. {order.total_amount.toFixed(2)}</span>
          </div>
        </div>

        {order.shipping_address && (
          <div className="bg-white/90 border border-white rounded-3xl p-8 shadow-xl mb-8">
            <h3 className="text-lg font-serif text-brand-darkSage mb-4 flex items-center gap-3">
              <div className="w-8 h-8 bg-brand-gold/10 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiTruck} className="text-brand-gold" size={16} />
              </div>
              Shipping Details
            </h3>

            {isOwner ? (
              <div className="text-sm text-brand-lightText space-y-1">
                <p className="font-bold text-brand-darkSage">{order.shipping_address.firstName} {order.shipping_address.lastName}</p>
                {order.shipping_address.addressLine1 && <p>{order.shipping_address.addressLine1}</p>}
                {order.shipping_address.addressLine2 && <p>{order.shipping_address.addressLine2}</p>}
                {order.shipping_address.streetName && <p>{order.shipping_address.streetName}</p>}
                {order.shipping_address.unitNumber && <p>Unit: {order.shipping_address.unitNumber}</p>}
                {order.shipping_address.address && !order.shipping_address.addressLine1 && <p>{order.shipping_address.address}</p>}
                <p>{order.shipping_address.city}{order.shipping_address.district ? `, ${order.shipping_address.district}` : ''}</p>
                <p>{order.shipping_address.countryCode} {order.shipping_address.phone}</p>
              </div>
            ) : (
              <div className="bg-brand-sand/50 rounded-2xl p-6 border border-brand-darkSage/5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-brand-gold/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                    <SafeIcon icon={FiLock} className="text-brand-gold" size={18} />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-brand-darkSage mb-1">Personal Information Protected</p>
                    <p className="text-xs text-brand-lightText leading-relaxed">
                      For your privacy, the recipient's name, address, and contact details are only visible to the account holder. 
                      Please check your order confirmation email for these details, or{' '}
                      <a href="/login" className="text-brand-gold font-bold underline underline-offset-2 hover:text-brand-darkSage transition-colors">
                        log in to your account
                      </a>{' '}
                      to view your complete shipping information.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {order.tracking_number && (
              <div className="mt-4 p-4 bg-brand-gold/5 rounded-xl border border-brand-gold/10">
                <p className="text-[10px] font-black uppercase tracking-widest text-brand-gold mb-1">Tracking Number</p>
                <p className="font-mono font-bold text-brand-darkSage">{order.tracking_number}</p>
              </div>
            )}
          </div>
        )}

        <div className="text-center text-xs text-brand-lightText/60 italic">
          This page auto-refreshes every 30 seconds
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default OrderTracking;